import { Canvas, useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { useMemo, useRef } from "react";
import * as THREE from "three";

/**
 * 3 morphing objects, only the active one is visible.
 * Each uses custom ShaderMaterial for richer visuals than stock PBR.
 *
 * step 0: Document (paper gradient + animated scan lines)
 * step 1: Brain / network (fresnel glow + pulsing noise)
 * step 2: Kit box (iridescent gradient shell)
 */

// ────────────────────────────── shared GLSL ──────────────────────────────
const fresnelChunk = /* glsl */ `
  float fresnel(vec3 normal, vec3 viewDir, float power){
    return pow(1.0 - max(dot(normalize(normal), normalize(viewDir)), 0.0), power);
  }
`;

const noiseChunk = /* glsl */ `
  float hash(vec3 p){ return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
  float noise3(vec3 p){
    vec3 i = floor(p); vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n000 = hash(i);
    float n100 = hash(i + vec3(1,0,0));
    float n010 = hash(i + vec3(0,1,0));
    float n110 = hash(i + vec3(1,1,0));
    float n001 = hash(i + vec3(0,0,1));
    float n101 = hash(i + vec3(1,0,1));
    float n011 = hash(i + vec3(0,1,1));
    float n111 = hash(i + vec3(1,1,1));
    return mix(
      mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
      mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y),
      f.z
    );
  }
`;

// ────────────────────────────── Document ──────────────────────────────
function Doc({ visible }: { visible: number }) {
  const g = useRef<THREE.Group>(null!);
  const mat = useRef<THREE.ShaderMaterial>(null!);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uVisible: { value: 0 },
    }),
    []
  );

  useFrame((s) => {
    g.current.rotation.y = Math.sin(s.clock.elapsedTime * 0.4) * 0.3;
    uniforms.uTime.value = s.clock.elapsedTime;
    uniforms.uVisible.value = visible;
  });

  return (
    <group ref={g} scale={visible}>
      <mesh>
        <planeGeometry args={[1.6, 2.2, 32, 32]} />
        <shaderMaterial
          ref={mat}
          uniforms={uniforms}
          transparent
          side={THREE.DoubleSide}
          vertexShader={/* glsl */ `
            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vView;
            uniform float uTime;
            void main(){
              vUv = uv;
              vNormal = normalMatrix * normal;
              vec3 pos = position;
              // soft paper wave
              pos.z += sin(uv.y * 8.0 + uTime * 1.2) * 0.01;
              vec4 mv = modelViewMatrix * vec4(pos, 1.0);
              vView = -mv.xyz;
              gl_Position = projectionMatrix * mv;
            }
          `}
          fragmentShader={/* glsl */ `
            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vView;
            uniform float uTime;
            uniform float uVisible;
            ${fresnelChunk}
            void main(){
              // paper base gradient
              vec3 warm = vec3(0.961, 0.953, 0.914);
              vec3 cool = vec3(0.78, 0.82, 0.74);
              vec3 col = mix(cool, warm, smoothstep(0.0, 1.0, vUv.y));

              // animated scan lines (text rows flowing)
              float lines = smoothstep(0.48, 0.5, abs(fract(vUv.y * 10.0 - uTime * 0.15) - 0.5));
              col = mix(col, vec3(0.290, 0.420, 0.239), (1.0 - lines) * smoothstep(0.1, 0.95, vUv.x) * 0.55);

              // edge glow
              float f = fresnel(vNormal, vView, 2.0);
              col += vec3(0.7, 0.85, 0.6) * f * 0.35;

              gl_FragColor = vec4(col, uVisible);
            }
          `}
        />
      </mesh>
    </group>
  );
}

// ────────────────────────────── Brain ──────────────────────────────
function Brain({ visible }: { visible: number }) {
  const g = useRef<THREE.Group>(null!);
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uVisible: { value: 0 },
    }),
    []
  );

  const orbitals = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const phi = Math.acos(1 - 2 * ((i + 0.5) / 24));
        const theta = Math.PI * (1 + Math.sqrt(5)) * i;
        return [
          Math.cos(theta) * Math.sin(phi),
          Math.sin(theta) * Math.sin(phi),
          Math.cos(phi),
        ] as [number, number, number];
      }),
    []
  );

  useFrame((s) => {
    g.current.rotation.y = s.clock.elapsedTime * 0.35;
    g.current.rotation.x = Math.sin(s.clock.elapsedTime * 0.2) * 0.2;
    uniforms.uTime.value = s.clock.elapsedTime;
    uniforms.uVisible.value = visible;
  });

  return (
    <group ref={g} scale={visible}>
      {/* Outer wireframe shell */}
      <mesh>
        <icosahedronGeometry args={[0.95, 1]} />
        <shaderMaterial
          uniforms={uniforms}
          transparent
          wireframe
          vertexShader={/* glsl */ `
            varying vec3 vNormal;
            varying vec3 vView;
            uniform float uTime;
            void main(){
              vNormal = normalMatrix * normal;
              vec3 pos = position;
              pos += normal * sin(uTime * 1.5 + position.y * 3.0) * 0.04;
              vec4 mv = modelViewMatrix * vec4(pos, 1.0);
              vView = -mv.xyz;
              gl_Position = projectionMatrix * mv;
            }
          `}
          fragmentShader={/* glsl */ `
            varying vec3 vNormal;
            varying vec3 vView;
            uniform float uTime;
            uniform float uVisible;
            ${fresnelChunk}
            void main(){
              float f = fresnel(vNormal, vView, 1.4);
              vec3 a = vec3(0.290, 0.420, 0.239);
              vec3 b = vec3(0.659, 0.722, 0.604);
              vec3 col = mix(a, b, f);
              col += vec3(0.85, 0.95, 0.7) * pow(f, 3.0) * 1.2;
              // pulse
              col *= 0.85 + 0.25 * sin(uTime * 2.0);
              gl_FragColor = vec4(col, uVisible);
            }
          `}
        />
      </mesh>

      {/* Inner glowing core */}
      <mesh>
        <icosahedronGeometry args={[0.75, 3]} />
        <shaderMaterial
          uniforms={uniforms}
          transparent
          vertexShader={/* glsl */ `
            varying vec3 vNormal;
            varying vec3 vView;
            varying vec3 vWorld;
            uniform float uTime;
            void main(){
              vNormal = normalMatrix * normal;
              vec3 pos = position + normal * (sin(uTime * 0.9 + position.x * 4.0) * 0.03);
              vec4 mv = modelViewMatrix * vec4(pos, 1.0);
              vView = -mv.xyz;
              vWorld = position;
              gl_Position = projectionMatrix * mv;
            }
          `}
          fragmentShader={/* glsl */ `
            varying vec3 vNormal;
            varying vec3 vView;
            varying vec3 vWorld;
            uniform float uTime;
            uniform float uVisible;
            ${fresnelChunk}
            ${noiseChunk}
            void main(){
              float f = fresnel(vNormal, vView, 2.5);
              float n = noise3(vWorld * 2.5 + uTime * 0.4);
              vec3 core = mix(vec3(0.18, 0.36, 0.22), vec3(0.7, 0.9, 0.55), n);
              float alpha = (0.2 + f * 0.85) * uVisible;
              gl_FragColor = vec4(core, alpha);
            }
          `}
        />
      </mesh>

      {/* Orbital nodes */}
      {orbitals.map((p, i) => (
        <mesh key={i} position={[p[0] * 1.35, p[1] * 1.35, p[2] * 1.35]}>
          <sphereGeometry args={[0.045, 12, 12]} />
          <meshBasicMaterial color="#2E4A24" transparent opacity={visible} />
        </mesh>
      ))}
    </group>
  );
}

// ────────────────────────────── Kit ──────────────────────────────
function Kit({ visible }: { visible: number }) {
  const g = useRef<THREE.Group>(null!);
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uVisible: { value: 0 },
    }),
    []
  );

  useFrame((s) => {
    g.current.rotation.y = s.clock.elapsedTime * 0.4;
    g.current.rotation.x = 0.25;
    uniforms.uTime.value = s.clock.elapsedTime;
    uniforms.uVisible.value = visible;
  });

  // Iridescent shader reused for box + lid + capsules
  const iridescent = {
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vView;
      varying vec3 vWorld;
      void main(){
        vNormal = normalMatrix * normal;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vView = -mv.xyz;
        vWorld = position;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vView;
      varying vec3 vWorld;
      uniform float uTime;
      uniform float uVisible;
      ${fresnelChunk}
      void main(){
        float f = fresnel(vNormal, vView, 1.8);
        // iridescent hue shift along view angle
        float t = f + sin(vWorld.y * 3.0 + uTime * 0.8) * 0.15;
        vec3 a = vec3(0.290, 0.420, 0.239);
        vec3 b = vec3(0.659, 0.722, 0.604);
        vec3 c = vec3(0.95, 0.95, 0.8);
        vec3 col = mix(a, b, smoothstep(0.0, 0.5, t));
        col = mix(col, c, smoothstep(0.5, 1.0, t) * 0.75);
        col += vec3(0.8, 0.95, 0.6) * pow(f, 4.0) * 0.9;
        gl_FragColor = vec4(col, uVisible);
      }
    `,
  };

  return (
    <group ref={g} scale={visible}>
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[1.8, 1.1, 1.3]} />
        <shaderMaterial uniforms={uniforms} transparent {...iridescent} />
      </mesh>
      <mesh position={[0, 0.55, 0]}>
        <boxGeometry args={[1.82, 0.1, 1.32]} />
        <shaderMaterial uniforms={uniforms} transparent {...iridescent} />
      </mesh>
      {[-0.5, 0, 0.5].map((x, i) => (
        <mesh key={i} position={[x, 0, 0.66]}>
          <cylinderGeometry args={[0.18, 0.18, 0.7, 24]} />
          <shaderMaterial uniforms={uniforms} transparent {...iridescent} />
        </mesh>
      ))}
    </group>
  );
}

// ────────────────────────────── Scene ──────────────────────────────
export function PlanScene({ step }: { step: number }) {
  const w = [0, 1, 2].map((i) => Math.max(0, 1 - Math.abs(step - i)));

  return (
    <Canvas
      className="!absolute inset-0"
      camera={{ position: [0, 0, 4.2], fov: 50 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[3, 4, 5]} intensity={1.1} color="#EFEDE2" />
      <pointLight position={[-3, 2, 2]} intensity={0.8} color="#A8B89A" />
      <Float floatIntensity={0.6} rotationIntensity={0.3} speed={1.4}>
        <Doc visible={w[0]!} />
        <Brain visible={w[1]!} />
        <Kit visible={w[2]!} />
      </Float>
    </Canvas>
  );
}
